import { Label } from "@radix-ui/react-label";
import { forwardRef, ReactNode } from "react";
import { cn } from "../lib/utils";

interface InputProps {
  label: string;
  id: string;
  rounded?: boolean;
  error?: string;
  helper?: string;
  slotBefore?: ReactNode;
}

export const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & InputProps
>(
  (
    {
      label,
      id,
      rounded = false,
      error,
      helper,
      slotBefore,
      className,
      ...props
    },
    ref,
  ) => {
    const isError = !!error;

    return (
      <div className="relative">
        <div className="flex">
          {slotBefore}

          <div className="relative flex-grow">
            <input
              ref={ref}
              id={id}
              aria-invalid={isError}
              aria-describedby={
                isError ? `${id}-error` : helper ? `${id}-helper` : undefined
              }
              className={cn(
                // Base
                "peer h-14 w-full bg-white [font-size:min(1rem,16px)] lg:h-16",
                // Border and padding
                "border-gray-border border-2 pt-4 pr-3 pl-4 lg:pl-6",
                // Typography
                "bb-text-body leading-0",
                // Transitions
                "transition-all",
                // Focus states
                "focus:border-gray-2 focus:outline-none",
                // Disabled state
                "disabled:bg-gray-1 disabled:cursor-not-allowed",
                // Conditional styles
                {
                  "border-error focus:border-error": isError,
                  "rounded-4xl pl-6": rounded,
                  "rounded-md": !rounded,
                },
                className,
              )}
              placeholder=" "
              {...props}
            />

            <Label
              htmlFor={id}
              className={cn(
                /**
                 * TODO
                 * A style for `label {}` without any class exists in the Gatsby project, so we need to
                 * do some unsetting and !important in order to use this component as we want to.
                 * When we move away from Gatsby, we should remove the unsets and !important
                 */
                "![font-family:unset] ![font-size:unset] !leading-6 ![font-weight:unset]",
                // Base
                "bb-text-body text-gray-2 pointer-events-none absolute max-w-[calc(100%-2rem)] truncate overflow-hidden !normal-case",
                // Animation
                "transform-gpu transition-all ease-in-out",
                // Default
                "translate-y-4 lg:translate-y-5",
                // Focus state
                "peer-focus:!bb-text-tiny peer-focus:translate-y-2",
                // Filled state
                "peer-[:not(:placeholder-shown)]:!bb-text-tiny peer-[:not(:placeholder-shown)]:translate-y-2",
                // Conditional styles
                {
                  "left-4.5 lg:left-6.5": !rounded,
                  "left-6.5": rounded,
                },
              )}
            >
              {label}
            </Label>
          </div>
        </div>

        {isError && (
          <div
            id={`${id}-error`}
            className="text-error bb-text-tiny mt-1 ml-0.5"
            role="alert"
          >
            {error}
          </div>
        )}

        {helper && !isError && (
          <div
            id={`${id}-helper`}
            className="bb-text-tiny text-gray-2 mt-1 ml-0.5"
          >
            {helper}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
